import { useEffect, useMemo, useState } from 'react';
import { Icon } from '../../components/Icon.tsx';
import { Button, Dialog, EmptyState, Field, IconButton, Skeleton, StatusBadge, Switch } from '../../components/ui.tsx';
import { t, tn, type MessageKey } from '../../i18n/index.ts';
import { action } from '../../lib/api.ts';
import { dateTime, money, qty as fmtQty } from '../../lib/format.ts';
import { usePlaces } from '../../lib/places.tsx';
import { useSession } from '../../lib/session.tsx';
import type { Place } from '../../lib/types.ts';
import { draftPacks, PacksEditor, packsError, PackSelect, savedPacks, unitsOf, type DraftPack, type Pack } from '../../components/Packs.tsx';
import { ManageLayout, useAction, useSave } from './common.tsx';
import { emptyLots, LotPick, LotsDialog, LotsField, lotsText, readLots, type LotDraft, type MoveLot, type Tracking } from './Lots.tsx';

export const UNITS = ['pcs', 'kg', 'g', 'l', 'ml', 'm', 'm2', 'm3', 't'] as const;
export type Unit = (typeof UNITS)[number];
export interface StockItem {
  id: string;
  name: string;
  unit: Unit;
  unit_cost: number;
  on_hand: number;
  min_qty: number;
  supplier: string | null;
  active: boolean;
  tracking: Tracking;
  /** Larger units it is bought and counted in (a box of 24). */
  packs?: Pack[];
  value: number;
  low: boolean;
  out: boolean;
  recipes: number;
  /** False at a location the item was never brought to: not stocked there, so never "out". */
  kept?: boolean;
  /** The whole business, when `on_hand` is one location's. */
  total_on_hand?: number;
  /** Each open location, when there is more than one. */
  by_location?: { location: string; on_hand: number; min_qty: number; kept?: boolean }[];
}
export interface Overview {
  location?: string | null;
  items: StockItem[];
  totals: { value: number; low: number; out: number; items: number; expiring?: number; expired?: number };
}
interface Move {
  id: string;
  item: string;
  item_name: string;
  unit: Unit;
  kind: 'sale' | 'purchase' | 'waste' | 'count' | 'transfer' | 'use' | 'consume' | 'produce' | 'landed';
  qty: number;
  value: number;
  balance: number;
  reason: string | null;
  document: string | null;
  at: string;
  by_name: string | null;
  order_name: string | null;
  receipt_no: number | null;
  location_code?: string | null;
  lots?: MoveLot[] | null;
}

export const unitLabel = (u: Unit) => t(`stock.unit_${u}` as MessageKey);
export const withUnit = (n: number, u: Unit) => `${fmtQty(n)} ${unitLabel(u)}`;
/** Parses "1,5" or "1.5" into a number with up to 3 decimals, or null. */
export function parseQty(text: string): number | null {
  const clean = text.replace(/\s/g, '').replace(',', '.');
  if (!/^\d+(\.\d{0,3})?$/.test(clean)) return null;
  return Number.parseFloat(clean);
}
/** A cost per unit typed in lek ("2.6") into minor units with decimals (260). */
export const parseCost = (text: string) => {
  const n = parseQty(text);
  return n === null ? null : Math.round(n * 100 * 1000) / 1000;
};
export const costText = (minor: number) => fmtQty(minor / 100);
/** Cost of one unit, e.g. "2.60 L / g". */
export const unitCost = (minor: number, u: Unit) => t('stock.per_unit', { cost: moneyFine(minor), unit: unitLabel(u) });
/** Money with up to 3 decimals for tiny unit costs. */
function moneyFine(minor: number) {
  return minor >= 100 || minor === 0 ? money(Math.round(minor)) : `${fmtQty(minor / 100)} L`;
}

const MOVE: Record<Move['kind'], { icon: 'receipt' | 'plus' | 'trash' | 'list' | 'transfer' | 'truck' | 'layers' | 'factory'; label: MessageKey }> = {
  sale: { icon: 'receipt', label: 'stock.kind_sale' },
  purchase: { icon: 'plus', label: 'stock.kind_purchase' },
  waste: { icon: 'trash', label: 'stock.kind_waste' },
  count: { icon: 'list', label: 'stock.kind_count' },
  transfer: { icon: 'transfer', label: 'stock.kind_transfer' },
  use: { icon: 'truck', label: 'stock.kind_use' },
  consume: { icon: 'layers', label: 'stock.kind_consume' },
  produce: { icon: 'factory', label: 'stock.kind_produce' },
  landed: { icon: 'truck', label: 'stock.kind_landed' },
};

function StockBadge({ item }: { item: StockItem }) {
  if (!item.active) return <StatusBadge tone="neutral" icon="archive" label={t('stock.archived')} />;
  if (item.kept === false) return <StatusBadge tone="neutral" icon="minus" label={t('stock.not_kept')} />;
  if (item.out) return <StatusBadge tone="danger" icon="alert" label={t('stock.out')} />;
  if (item.low) return <StatusBadge tone="warn" icon="clock" label={t('stock.low')} />;
  return <StatusBadge tone="ok" icon="check" label={t('stock.in_stock')} />;
}

type Filter = 'all' | 'low' | 'archived';

/** What is on the shelves, what it is worth, and every movement in and out. */
export function Stock() {
  const { can } = useSession();
  const places = usePlaces();
  // One location's shelves, or '' for the whole business.
  const [place, setPlace] = useState('');
  const at = places.multi ? place : '';
  const { data, reload } = useAction<Overview>('inventory.overview', at ? { location: at } : {});
  const [shown, setShown] = useState(15);
  const { data: moves, reload: reloadMoves } = useAction<Move[]>('inventory.moves', { limit: shown + 1, ...(at ? { location: at } : {}) });
  const [filter, setFilter] = useState<Filter>('all');
  const [search, setSearch] = useState('');
  const [dialog, setDialog] = useState<'receive' | 'count' | 'new' | 'transfer' | 'lots' | 'expiring' | 'costing' | null>(null);
  const { data: setup, reload: reloadSetup } = useAction<{ costing: Costing; costing_since: string | null }>('inventory.settings', {});
  const [open, setOpen] = useState<StockItem | null>(null);
  const manage = can('inventory.manage');
  const counts = can('inventory.count');
  // Lots show once any item is tracked, or any lot is left from before.
  const tracked = !!data && (data.items.some((i) => i.tracking !== 'none') || !!data.totals.expiring || !!data.totals.expired);
  const refresh = async () => {
    await Promise.all([reload(), reloadMoves()]);
  };

  const items = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data?.items ?? [])
      .filter((i) => (filter === 'archived' ? !i.active : i.active))
      .filter((i) => filter !== 'low' || i.low || i.out)
      .filter((i) => !q || i.name.toLowerCase().includes(q) || (i.supplier ?? '').toLowerCase().includes(q));
  }, [data, filter, search]);

  return (
    <ManageLayout
      title={t('manage.stock')}
      subtitle={t('stock.subtitle')}
      actions={
        <>
          {manage ? (
            <Button variant="ghost" icon="sliders" onClick={() => setDialog('costing')} disabled={!setup}>
              {setup ? t(`stock.costing_${setup.costing}`) : t('stock.costing')}
            </Button>
          ) : null}
          {manage && places.multi ? (
            <Button icon="transfer" onClick={() => setDialog('transfer')} disabled={!data}>
              {t('stock.transfer')}
            </Button>
          ) : null}
          {counts ? (
            <Button icon="list" onClick={() => setDialog('count')} disabled={!data}>
              {t('stock.count')}
            </Button>
          ) : null}
          {manage ? (
            <Button variant="primary" icon="plus" onClick={() => setDialog('receive')} disabled={!data}>
              {t('stock.receive')}
            </Button>
          ) : null}
        </>
      }
    >
      {!data ? (
        <Skeleton h={420} r="var(--radius-lg)" />
      ) : (
        <div className="stock">
          <dl className={`fiscal-stats${tracked ? ' five' : ''}`}>
            <div className="fiscal-stat">
              <dt>{t('stock.value')}</dt>
              <dd>{money(data.totals.value)}</dd>
            </div>
            <div className="fiscal-stat">
              <dt>{t('stock.items')}</dt>
              <dd>{data.totals.items}</dd>
            </div>
            <div className={`fiscal-stat${data.totals.low ? ' warn' : ''}`}>
              <dt>{t('stock.running_low')}</dt>
              <dd>{data.totals.low}</dd>
            </div>
            <div className={`fiscal-stat${data.totals.out ? ' danger' : ''}`}>
              <dt>{t('stock.out_of_stock')}</dt>
              <dd>{data.totals.out}</dd>
            </div>
            {tracked ? (
              <div className={`fiscal-stat${data.totals.expired ? ' danger' : data.totals.expiring ? ' warn' : ''}`}>
                <dt>{t('lots.stat')}</dt>
                <dd>
                  <button type="button" className="link-button lot-stat" onClick={() => setDialog('expiring')} aria-label={t('lots.stat_open')}>
                    {(data.totals.expiring ?? 0) + (data.totals.expired ?? 0)}
                  </button>
                  {data.totals.expired ? <span className="lot-stat-sub">{tn('lots.stat_expired', data.totals.expired)}</span> : null}
                </dd>
              </div>
            ) : null}
          </dl>

          <div className="toolbar">
            {places.multi ? (
              <select className="input place-select" value={place} onChange={(e) => setPlace(e.target.value)} aria-label={t('places.location')}>
                <option value="">{t('places.all')}</option>
                {places.places.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            ) : null}
            <div className="segmented" role="radiogroup" aria-label={t('stock.show')}>
              {(['all', 'low', 'archived'] as const).map((f) => (
                <button key={f} role="radio" aria-checked={filter === f} className={filter === f ? 'active' : ''} onClick={() => setFilter(f)}>
                  {t(`stock.filter_${f}`)}
                </button>
              ))}
            </div>
            {tracked ? (
              <Button icon="hash" onClick={() => setDialog('lots')}>
                {t('lots.button')}
              </Button>
            ) : null}
            <div className="search grow">
              <Icon name="search" size={18} />
              <input className="input" type="search" placeholder={t('stock.search')} aria-label={t('stock.search')} value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
          </div>

          <section className="list-card" aria-labelledby="stock-items-title">
            <div className="list-card-head">
              <div>
                <h2 id="stock-items-title">{t('stock.items_title')}</h2>
                <p className="muted">{t('stock.items_hint')}</p>
              </div>
              {manage ? (
                <Button icon="plus" onClick={() => setDialog('new')}>
                  {t('stock.new_item')}
                </Button>
              ) : null}
            </div>
            {items.length === 0 ? (
              <EmptyState icon="box" title={search ? t('order.no_results') : filter === 'low' ? t('stock.none_low') : t('stock.empty')} />
            ) : (
              items.map((i) => (
                <button key={i.id} className={`list-row stock-row${i.active ? '' : ' archived'}`} onClick={() => setOpen(i)}>
                  <span className="list-icon unit-chip" aria-hidden="true">
                    {unitLabel(i.unit)}
                  </span>
                  <span className="list-main">
                    <span className="list-title">{i.name}</span>
                    <span className="list-sub">
                      {[unitCost(i.unit_cost, i.unit), i.supplier, i.recipes ? tn('stock.in_recipes', i.recipes) : null, i.tracking !== 'none' ? t(`lots.tracked_${i.tracking}`) : null]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                    {!at && i.by_location?.length ? <PlaceSplit item={i} places={places.places} /> : null}
                  </span>
                  <span className="stock-qty">
                    <strong className={i.out ? 'danger' : i.low ? 'warn' : i.kept === false ? 'muted' : ''}>{withUnit(i.on_hand, i.unit)}</strong>
                    <span className="muted">{money(i.value)}</span>
                  </span>
                  <StockBadge item={i} />
                </button>
              ))
            )}
          </section>

          <section className="list-card" aria-labelledby="stock-moves-title">
            <div className="list-card-head">
              <div>
                <h2 id="stock-moves-title">{t('stock.moves')}</h2>
                <p className="muted">{t('stock.moves_hint')}</p>
              </div>
            </div>
            {!moves ? <Skeleton h={120} /> : moves.length === 0 ? <EmptyState icon="list" title={t('stock.no_moves')} /> : moves.slice(0, shown).map((m) => <MoveRow key={m.id} m={m} place={places.multi && !at} />)}
            {moves && moves.length > shown ? (
              <div className="list-more">
                <Button icon="list" onClick={() => setShown(shown + 30)}>
                  {t('stock.show_more')}
                </Button>
              </div>
            ) : null}
          </section>
        </div>
      )}

      {dialog === 'receive' && data ? (
        <ReceiveDialog items={data.items.filter((i) => i.active)} places={places.multi ? places.places : []} place={at || places.here?.id || ''} onClose={() => setDialog(null)} onDone={refresh} />
      ) : null}
      {dialog === 'count' && data ? (
        // Counting compares with what one location should hold, so it needs one.
        <CountDialog
          items={data.items.filter((i) => i.active)}
          places={places.multi ? places.places : []}
          place={at || places.here?.id || ''}
          stale={places.multi && (data.location ?? '') !== (at || places.here?.id || '')}
          onPlace={setPlace}
          onClose={() => setDialog(null)}
          onDone={refresh}
        />
      ) : null}
      {dialog === 'costing' && setup ? (
        <CostingDialog
          costing={setup.costing}
          since={setup.costing_since}
          onClose={() => setDialog(null)}
          onDone={async () => {
            await Promise.all([refresh(), reloadSetup()]);
          }}
        />
      ) : null}
      {dialog === 'lots' || dialog === 'expiring' ? (
        <LotsDialog places={places.places} location={at || undefined} start={dialog === 'expiring' ? 'expiring' : 'in_stock'} onClose={() => setDialog(null)} />
      ) : null}
      {dialog === 'transfer' && data ? <TransferDialog places={places.places} from={at || places.here?.id || ''} onClose={() => setDialog(null)} onDone={refresh} /> : null}
      {dialog === 'new' ? <ItemDialog places={places.multi ? places.places : []} place={at || places.here?.id || ''} onClose={() => setDialog(null)} onDone={refresh} /> : null}
      {open ? (
        <ItemDialog
          item={data?.items.find((i) => i.id === open.id) ?? open}
          places={places.multi ? places.places : []}
          place={at || places.here?.id || ''}
          manage={manage}
          counts={counts}
          costing={setup?.costing ?? 'average'}
          onClose={() => setOpen(null)}
          onDone={refresh}
        />
      ) : null}
    </ManageLayout>
  );
}

/** How much each location holds, under an item, when the list shows the whole business. */
function PlaceSplit({ item, places }: { item: StockItem; places: Place[] }) {
  return (
    <span className="place-split">
      {item.by_location!.map((b) => {
        const p = places.find((x) => x.id === b.location);
        if (!p || b.kept === false) return null;
        const low = b.min_qty > 0 && b.on_hand < b.min_qty;
        return (
          <span key={b.location} className={`place-split-item${b.on_hand <= 0 ? ' danger' : low ? ' warn' : ''}`} title={p.name}>
            <span className="place-tag">{p.code}</span> {withUnit(b.on_hand, item.unit)}
          </span>
        );
      })}
    </span>
  );
}

function MoveRow({ m, compact, place }: { m: Move; compact?: boolean; place?: boolean }) {
  const meta = MOVE[m.kind];
  const where =
    m.kind === 'sale' && m.order_name
      ? t('stock.sold_on', { name: m.order_name, n: m.receipt_no ?? '' })
      : [m.document, m.reason].filter(Boolean).join(' · ');
  const lots = lotsText(m.lots);
  return (
    <div className={`list-row${compact ? ' compact-move' : ''}`}>
      <span className={`list-icon move-${m.kind}`} aria-hidden="true">
        <Icon name={meta.icon} size={18} />
      </span>
      <span className="list-main">
        <span className="list-title">
          {compact ? t(meta.label) : `${m.item_name} · ${t(meta.label)}`}
        </span>
        <span className="list-sub">
          {place && m.location_code ? <span className="place-tag">{m.location_code}</span> : null}
          {[dateTime(m.at), m.by_name, where, lots ? t('lots.moved', { lots }) : null].filter(Boolean).join(' · ')}
        </span>
      </span>
      {m.kind === 'landed' ? (
        // No goods move: the cost of what is on the shelves goes up.
        <span className="stock-qty">
          <strong className="ok">+{money(m.value)}</strong>
          <span className="muted">{t('stock.landed_sub')}</span>
        </span>
      ) : (
        <span className="stock-qty">
          <strong className={m.qty < 0 ? '' : 'ok'}>
            {m.qty > 0 ? '+' : '−'}
            {withUnit(Math.abs(m.qty), m.unit)}
          </strong>
          <span className="muted">{t('stock.left', { qty: withUnit(m.balance, m.unit) })}</span>
        </span>
      )}
    </div>
  );
}

/** Goods from a supplier: several items at once, each with what was paid per unit. */
/** Choose where something happens, when the business has more than one location. */
function PlaceField({ places, value, onChange, label, hint }: { places: Place[]; value: string; onChange: (v: string) => void; label?: string; hint?: string }) {
  if (!places.length) return null;
  return (
    <Field label={label ?? t('places.location')} hint={hint}>
      <select className="input" value={value} onChange={(e) => onChange(e.target.value)}>
        {places.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name}
          </option>
        ))}
      </select>
    </Field>
  );
}

function ReceiveDialog({ items, places, place, onClose, onDone }: { items: StockItem[]; places: Place[]; place: string; onClose: () => void; onDone: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [where, setWhere] = useState(place);
  const [supplier, setSupplier] = useState('');
  // `pack`: what the quantity and cost are typed in (a box of 24); '' is the item's own unit.
  const [lines, setLines] = useState<{ item: string; qty: string; cost: string; pack: string; lots: LotDraft }[]>([{ item: '', qty: '', cost: '', pack: '', lots: emptyLots() }]);
  const byId = new Map(items.map((i) => [i.id, i]));
  const parsed = lines.map((l) => {
    const item = byId.get(l.item);
    const qty = parseQty(l.qty);
    // Lot numbers are given for the units that arrive, whatever they were counted in.
    const units = item && qty !== null ? unitsOf(item.packs ?? [], qty, l.pack) : qty;
    return { item, qty, cost: parseCost(l.cost), lots: item ? readLots(item.tracking, units, l.lots) : {}, units };
  });
  const ready = parsed.filter((l) => l.item && l.qty && l.qty > 0 && l.cost !== null);
  const total = ready.reduce((s, l) => s + Math.round(l.qty! * l.cost!), 0);
  const invalid = parsed.some((l, i) => (lines[i]!.qty || lines[i]!.cost || lines[i]!.item) && !(l.item && l.qty && l.qty > 0 && l.cost !== null && !l.lots.problem));
  const set = (i: number, k: 'item' | 'qty' | 'cost' | 'pack', v: string) =>
    setLines(
      lines.map((l, j) => {
        if (j !== i) return l;
        const next = { ...l, [k]: v };
        // Picking an item fills in what it cost last time; picking a pack, what a pack of it cost.
        if (k === 'item') next.pack = '';
        if (k === 'item' && !l.cost && byId.get(v)) next.cost = costText(byId.get(v)!.unit_cost);
        if (k === 'pack' && byId.get(l.item)) next.cost = costText(Math.round(unitsOf(byId.get(l.item)!.packs ?? [], 1, v) * byId.get(l.item)!.unit_cost * 1000) / 1000);
        return next;
      }),
    );
  const save = () =>
    void run(async () => {
      await action('inventory.receive', {
        supplier: supplier.trim() || undefined,
        ...(places.length ? { location: where } : {}),
        lines: ready.map((l) => {
          const pack = lines[parsed.indexOf(l)]!.pack;
          return { item: l.item!.id, qty: l.qty, unit_cost: l.cost, ...(pack ? { pack } : {}), ...(l.lots.lots ? { lots: l.lots.lots } : {}) };
        }),
      });
      await onDone();
      onClose();
    }, t('stock.received', { total: money(total) }));

  return (
    <Dialog
      title={t('stock.receive')}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <span className="muted">{t('stock.receive_total', { total: money(total) })}</span>
          <Button variant="primary" icon="check" onClick={save} busy={busy} disabled={!ready.length || invalid}>
            {t('stock.receive_save')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <PlaceField places={places} value={where} onChange={setWhere} label={t('stock.deliver_to')} />
        <Field label={t('stock.supplier')} hint={t('stock.supplier_hint')}>
          <input className="input" value={supplier} maxLength={120} onChange={(e) => setSupplier(e.target.value)} list="stock-suppliers" />
        </Field>
        <datalist id="stock-suppliers">
          {[...new Set(items.map((i) => i.supplier).filter(Boolean))].map((s) => (
            <option key={s} value={s!} />
          ))}
        </datalist>
        <div className="stock-lines" role="table" aria-label={t('stock.receive_lines')}>
          <div className="stock-line head" role="row">
            <span role="columnheader">{t('stock.item')}</span>
            <span role="columnheader">{t('stock.quantity')}</span>
            <span role="columnheader">{t('stock.cost_per_unit')}</span>
            <span role="columnheader" className="num">
              {t('stock.line_total')}
            </span>
            <span />
          </div>
          {lines.map((l, i) => {
            const p = parsed[i]!;
            return (
              <div key={i} className="stock-line-wrap">
                <div className="stock-line" role="row">
                  <select className="input" aria-label={t('stock.item')} value={l.item} onChange={(e) => set(i, 'item', e.target.value)} data-autofocus={i === 0 || undefined}>
                    <option value="">{t('stock.pick_item')}</option>
                    {items.map((it) => (
                      <option key={it.id} value={it.id}>
                        {it.name}
                      </option>
                    ))}
                  </select>
                  <span className="input-unit">
                    <input className="input" inputMode="decimal" aria-label={t('stock.quantity')} value={l.qty} onChange={(e) => set(i, 'qty', e.target.value)} placeholder="0" />
                    {p.item ? <PackSelect packs={p.item.packs ?? []} unitText={unitLabel(p.item.unit)} value={l.pack} label={t('packs.counted_in', { name: p.item.name })} onChange={(v) => set(i, 'pack', v)} /> : <span />}
                  </span>
                  <span className="input-unit">
                    <input className="input" inputMode="decimal" aria-label={t('stock.cost_per_unit')} value={l.cost} onChange={(e) => set(i, 'cost', e.target.value)} placeholder="0" />
                    <span>{p.item ? `L / ${l.pack || unitLabel(p.item.unit)}` : 'L'}</span>
                  </span>
                  <span className="num">{p.qty && p.cost !== null ? money(Math.round(p.qty * p.cost)) : '—'}</span>
                  <IconButton icon="trash" label={t('stock.remove_line')} disabled={lines.length === 1} onClick={() => setLines(lines.filter((_, j) => j !== i))} />
                </div>
              {p.item ? (
                <LotsField
                  name={p.item.name}
                  unit={p.item.unit}
                  tracking={p.item.tracking}
                  qty={p.units}
                  value={l.lots}
                  onChange={(d) => setLines(lines.map((x, j) => (j === i ? { ...x, lots: d } : x)))}
                />
              ) : null}
              </div>
            );
          })}
        </div>
        <Button icon="plus" className="add-line" onClick={() => setLines([...lines, { item: '', qty: '', cost: '', pack: '', lots: emptyLots() }])}>
          {t('stock.add_line')}
        </Button>
      </div>
    </Dialog>
  );
}

/** Walk the shelves and type what is there; only the items typed in are corrected. */
function CountDialog({
  items,
  places,
  place,
  stale,
  onPlace,
  onClose,
  onDone,
}: {
  items: StockItem[];
  places: Place[];
  /** The location being counted; the list behind follows it, so "expected" is that location's. */
  place: string;
  /** The list behind still shows another location's numbers. */
  stale?: boolean;
  onPlace: (id: string) => void;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const { busy, run } = useSave();
  useEffect(() => {
    if (places.length && place) onPlace(place);
  }, [places.length, place, onPlace]);
  // Loose units by item, and full packs by item and pack ("3 boxes and 5 bottles").
  const [counted, setCounted] = useState<Record<string, string>>({});
  const [packed, setPacked] = useState<Record<string, Record<string, string>>>({});
  const [search, setSearch] = useState('');
  /** What was typed for an item, in its unit: undefined when nothing, null when not a number. */
  const totalOf = (i: StockItem): number | null | undefined => {
    const parts = [counted[i.id] ?? '', ...(i.packs ?? []).map((p) => packed[i.id]?.[p.name] ?? '')];
    if (parts.every((x) => x === '')) return undefined;
    let sum = 0;
    for (const [k, raw] of parts.entries()) {
      if (raw === '') continue;
      const n = parseQty(raw);
      if (n === null) return null;
      sum += k === 0 ? n : n * i.packs![k - 1]!.qty;
    }
    return Math.round(sum * 1000) / 1000;
  };
  const filled = items.filter((i) => totalOf(i) !== undefined);
  const bad = filled.some((i) => totalOf(i) === null);
  const diffValue = filled.reduce((s, i) => {
    const n = totalOf(i);
    return n == null ? s : s + Math.round((n - i.on_hand) * i.unit_cost);
  }, 0);
  const q = search.trim().toLowerCase();
  const save = () =>
    void run(async () => {
      await action('inventory.count', { ...(places.length ? { location: place } : {}), lines: filled.map((i) => ({ item: i.id, counted: totalOf(i) })) });
      await onDone();
      onClose();
    }, tn('stock.counted', filled.length));
  return (
    <Dialog
      title={t('stock.count')}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <span className="muted">
            {filled.length ? t('stock.count_summary', { n: filled.length, value: money(diffValue, { sign: true }) }) : t('stock.count_hint')}
          </span>
          <Button variant="primary" icon="check" onClick={save} busy={busy} disabled={!filled.length || bad || stale}>
            {t('stock.count_save')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <div className="search">
          <Icon name="search" size={18} />
          <input className="input" type="search" placeholder={t('stock.search_items')} aria-label={t('stock.search_items')} value={search} onChange={(e) => setSearch(e.target.value)} data-autofocus />
        </div>
        <PlaceField
          places={places}
          value={place}
          onChange={(v) => {
            setCounted({});
            setPacked({});
            onPlace(v);
          }}
          label={t('stock.counting_at')}
        />
        {stale ? <Skeleton h={240} /> : null}
        <div className="count-list" hidden={stale}>
          {items
            .filter((i) => !q || i.name.toLowerCase().includes(q))
            .map((i) => {
              const raw = counted[i.id] ?? '';
              const total = totalOf(i);
              const n = total ?? null;
              const diff = n === null ? null : Math.round((n - i.on_hand) * 1000) / 1000;
              return (
                <div key={i.id} className="count-row">
                  <span className="list-main">
                    <span className="list-title">{i.name}</span>
                    <span className="list-sub">{t('stock.expected', { qty: withUnit(i.on_hand, i.unit) })}</span>
                  </span>
                  <span className="count-inputs">
                  {(i.packs ?? []).map((p) => {
                    const v = packed[i.id]?.[p.name] ?? '';
                    return (
                      <span key={p.name} className="input-unit">
                        <input
                          className={`input${v && parseQty(v) === null ? ' invalid' : ''}`}
                          inputMode="decimal"
                          aria-label={t('packs.counted_packs', { pack: p.name, name: i.name })}
                          value={v}
                          placeholder="—"
                          onChange={(e) => setPacked({ ...packed, [i.id]: { ...packed[i.id], [p.name]: e.target.value } })}
                        />
                        <span>{p.name}</span>
                      </span>
                    );
                  })}
                  <span className="input-unit">
                    <input
                      className={`input${raw && parseQty(raw) === null ? ' invalid' : ''}`}
                      inputMode="decimal"
                      aria-label={t('stock.counted_for', { name: i.name })}
                      value={raw}
                      placeholder="—"
                      onChange={(e) => setCounted({ ...counted, [i.id]: e.target.value })}
                    />
                    <span>{unitLabel(i.unit)}</span>
                  </span>
                  </span>
                  <span className={`count-diff${diff === null ? '' : diff < 0 ? ' danger' : diff > 0 ? ' ok' : ''}`}>
                    {diff === null ? '' : diff === 0 ? t('stock.no_difference') : `${diff > 0 ? '+' : '−'}${withUnit(Math.abs(diff), i.unit)}`}
                  </span>
                </div>
              );
            })}
        </div>
      </div>
    </Dialog>
  );
}

const WASTE_REASONS: MessageKey[] = ['stock.reason_expired', 'stock.reason_spoiled', 'stock.reason_dropped', 'stock.reason_staff'];

/** A new item, or one item's details, its recent movements and recording waste. */
function ItemDialog({
  item,
  places = [],
  place = '',
  manage = true,
  counts = true,
  costing = 'average',
  onClose,
  onDone,
}: {
  item?: StockItem;
  costing?: Costing;
  /** Open locations, when there is more than one: each has its own minimum and its own shelf. */
  places?: Place[];
  /** Where opening stock and waste go unless chosen otherwise. */
  place?: string;
  manage?: boolean;
  counts?: boolean;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const { busy, run } = useSave();
  const multi = places.length > 0;
  const [where, setWhere] = useState(place);
  const minAt = (id: string) => item?.by_location?.find((b) => b.location === id)?.min_qty ?? 0;
  const [mins, setMins] = useState<Record<string, string>>(() =>
    Object.fromEntries(places.map((p) => [p.id, item && minAt(p.id) ? fmtQty(minAt(p.id)) : ''])),
  );
  const minsN = Object.fromEntries(Object.entries(mins).map(([id, v]) => [id, v === '' ? 0 : parseQty(v)]));
  const minsBad = Object.values(minsN).some((v) => v === null);
  const minsDirty = places.some((p) => minsN[p.id] !== minAt(p.id));
  const { data: moves, reload } = useAction<Move[]>('inventory.moves', item ? { item: item.id, limit: 10 } : { limit: 1 });
  const [name, setName] = useState(item?.name ?? '');
  const [unit, setUnit] = useState<Unit>(item?.unit ?? 'pcs');
  const [min, setMin] = useState(item ? fmtQty(item.min_qty) : '');
  const [supplier, setSupplier] = useState(item?.supplier ?? '');
  const [active, setActive] = useState(item?.active ?? true);
  const [tracking, setTracking] = useState<Tracking>(item?.tracking ?? 'none');
  const [packs, setPacks] = useState<DraftPack[]>(() => draftPacks(item?.packs));
  const packsBad = packsError(unitLabel(unit), packs);
  const packsDirty = JSON.stringify(packsBad ? null : savedPacks(packs)) !== JSON.stringify(item?.packs ?? []);
  const [showLots, setShowLots] = useState(false);
  const [opening, setOpening] = useState('');
  const [cost, setCost] = useState('');
  const [waste, setWaste] = useState<{ qty: string; reason: string; lot: string } | null>(null);
  // Quantities already recorded are in this unit, so it is fixed once the item has movements.
  const unitLocked = !!item && (!moves || moves.length > 0);
  const minN = min === '' ? 0 : parseQty(min);
  const openingN = opening === '' ? 0 : parseQty(opening);
  const costN = cost === '' ? 0 : parseCost(cost);
  const invalid = !name.trim() || (multi ? minsBad : minN === null) || openingN === null || costN === null || !!packsBad;
  const dirty =
    !item ||
    name !== item.name ||
    unit !== item.unit ||
    (multi ? minsDirty : minN !== item.min_qty) ||
    supplier !== (item.supplier ?? '') ||
    active !== item.active ||
    tracking !== item.tracking ||
    packsDirty;

  const save = () =>
    void run(async () => {
      const values = { name: name.trim(), unit, supplier: supplier.trim() || null, active, tracking, packs: savedPacks(packs) };
      // With several locations each keeps its own minimum; the item's own is the first location's.
      let id = item?.id;
      if (item) await action('inventory.update_item', { item: item.id, ...values, ...(multi ? {} : { min_qty: minN }) });
      else {
        const made = await action<{ id: string }>('inventory.create_item', {
          ...values,
          min_qty: multi ? 0 : minN,
          opening: openingN,
          unit_cost: costN,
          ...(multi ? { location: where } : {}),
        });
        id = made.id;
      }
      for (const p of places) {
        if (minsN[p.id] !== minAt(p.id)) await action('inventory.set_location_min', { item: id, location: p.id, min_qty: minsN[p.id] });
      }
      await onDone();
      onClose();
    }, item ? t('stock.saved', { name: name.trim() }) : t('stock.added', { name: name.trim() }));

  const wasteN = waste ? parseQty(waste.qty) : null;
  const recordWaste = () =>
    void run(async () => {
      await action('inventory.waste', { item: item!.id, qty: wasteN, reason: waste!.reason.trim(), ...(waste!.lot ? { lot: waste!.lot } : {}), ...(multi ? { location: where } : {}) });
      setWaste(null);
      await Promise.all([onDone(), reload()]);
    }, t('stock.wasted', { qty: withUnit(wasteN ?? 0, item!.unit), name: item!.name }));

  return (
    <Dialog
      title={item ? item.name : t('stock.new_item')}
      onClose={onClose}
      size="md"
      footer={
        manage ? (
          <>
            <Button variant="ghost" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button variant="primary" icon="check" onClick={save} busy={busy} disabled={invalid || !dirty}>
              {item ? t('common.save') : t('stock.add_item')}
            </Button>
          </>
        ) : undefined
      }
    >
      <div className="form-stack">
        {item ? (
          <div className="item-summary">
            <div>
              <span className="muted">{t('stock.on_hand')}</span>
              <strong>{withUnit(item.on_hand, item.unit)}</strong>
            </div>
            <div>
              <span className="muted">{t('stock.worth')}</span>
              <strong>{money(item.value)}</strong>
            </div>
            <div>
              <span className="muted">{t('stock.avg_cost')}</span>
              <strong>{unitCost(item.unit_cost, item.unit)}</strong>
            </div>
            <StockBadge item={item} />
          </div>
        ) : null}
        {item && multi && item.by_location?.length ? <PlaceSplit item={item} places={places} /> : null}
        {item && item.tracking !== 'none' ? (
          <div>
            <Button size="sm" icon="hash" onClick={() => setShowLots(true)}>
              {t(item.tracking === 'serial' ? 'lots.see_serials' : 'lots.see_lots')}
            </Button>
          </div>
        ) : null}
        {item && costing === 'fifo' && item.on_hand > 0 ? <CostLayers item={item} /> : null}
        {showLots && item ? <LotsDialog places={places} item={item} onClose={() => setShowLots(false)} /> : null}

        {manage ? (
          <>
            <div className="form-grid">
              <Field label={t('stock.name')}>
                <input className="input" value={name} maxLength={80} onChange={(e) => setName(e.target.value)} data-autofocus={!item || undefined} />
              </Field>
              <Field label={t('stock.supplier')}>
                <input className="input" value={supplier} maxLength={120} onChange={(e) => setSupplier(e.target.value)} />
              </Field>
            </div>
            <fieldset className="option-group">
              <legend className="field-label">{t('stock.unit')}</legend>
              <div className="segmented" role="radiogroup" aria-label={t('stock.unit')}>
                {UNITS.map((u) => (
                  <button key={u} type="button" role="radio" aria-checked={unit === u} className={unit === u ? 'active' : ''} disabled={unitLocked && unit !== u} onClick={() => setUnit(u)}>
                    {t(`stock.unit_long_${u}` as MessageKey)}
                  </button>
                ))}
              </div>
              {unitLocked ? <p className="muted small">{t('stock.unit_locked')}</p> : null}
            </fieldset>
            <PacksEditor unitText={unitLabel(unit)} packs={packs} onChange={setPacks} error={packsBad} />
            <fieldset className="option-group">
              <legend className="field-label">{t('lots.tracking')}</legend>
              <div className="segmented" role="radiogroup" aria-label={t('lots.tracking')}>
                {(['none', 'lot', 'serial'] as const).map((k) => (
                  <button key={k} type="button" role="radio" aria-checked={tracking === k} className={tracking === k ? 'active' : ''} onClick={() => setTracking(k)}>
                    {t(`lots.track_${k}`)}
                  </button>
                ))}
              </div>
              <p className="muted small">{t(`lots.track_${tracking}_hint`)}</p>
            </fieldset>
            {multi ? (
              <fieldset className="option-group">
                <legend className="field-label">
                  {t('stock.min_qty')} <span className="muted">{t('stock.min_per_place_hint')}</span>
                </legend>
                <div className="form-grid">
                  {places.map((p) => (
                    <Field key={p.id} label={p.name} error={minsN[p.id] === null ? t('stock.number_rule') : null}>
                      <span className="input-unit">
                        <input className="input" inputMode="decimal" value={mins[p.id] ?? ''} onChange={(e) => setMins({ ...mins, [p.id]: e.target.value })} placeholder="0" />
                        <span>{unitLabel(unit)}</span>
                      </span>
                    </Field>
                  ))}
                </div>
              </fieldset>
            ) : null}
            <div className="form-grid">
              {multi ? null : (
                <Field label={t('stock.min_qty')} hint={t('stock.min_qty_hint')} error={minN === null ? t('stock.number_rule') : null}>
                  <input className="input" inputMode="decimal" value={min} onChange={(e) => setMin(e.target.value)} placeholder="0" />
                </Field>
              )}
              {!item ? (
                <>
                  {multi ? <PlaceField places={places} value={where} onChange={setWhere} label={t('stock.opening_at')} /> : null}
                  <Field label={t('stock.opening')} hint={t('stock.opening_hint')} error={openingN === null ? t('stock.number_rule') : null}>
                    <input className="input" inputMode="decimal" value={opening} onChange={(e) => setOpening(e.target.value)} placeholder="0" />
                  </Field>
                  <Field label={t('stock.cost_per', { unit: unitLabel(unit) })} hint={t('stock.cost_hint')} error={costN === null ? t('stock.number_rule') : null}>
                    <input className="input" inputMode="decimal" value={cost} onChange={(e) => setCost(e.target.value)} placeholder="0" />
                  </Field>
                </>
              ) : null}
            </div>
            {item ? (
              <label className="switch-row">
                <span>
                  <span className="switch-title">{t('stock.in_use')}</span>
                  <span className="muted">{t('stock.in_use_hint')}</span>
                </span>
                <Switch checked={active} label={t('stock.in_use')} onChange={setActive} />
              </label>
            ) : null}
          </>
        ) : null}

        {item ? (
          <section className="item-moves" aria-labelledby="item-moves-title">
            <div className="item-moves-head">
              <h3 id="item-moves-title">{t('stock.recent')}</h3>
              {!waste && counts ? (
                <Button icon="trash" size="sm" onClick={() => setWaste({ qty: '', reason: '', lot: '' })}>
                  {t('stock.record_waste')}
                </Button>
              ) : null}
            </div>
            {waste ? (
              <div className="waste-form">
                <div className="form-grid">
                  <Field label={t('stock.waste_qty', { unit: unitLabel(item.unit) })} error={waste.qty && wasteN === null ? t('stock.number_rule') : null}>
                    <input className="input" inputMode="decimal" value={waste.qty} onChange={(e) => setWaste({ ...waste, qty: e.target.value })} data-autofocus />
                  </Field>
                  <Field label={t('stock.waste_reason')}>
                    <input className="input" value={waste.reason} maxLength={200} onChange={(e) => setWaste({ ...waste, reason: e.target.value })} />
                  </Field>
                </div>
                {multi ? <PlaceField places={places} value={where} onChange={setWhere} label={t('stock.waste_at')} /> : null}
                {item.tracking !== 'none' ? <LotPick item={item.id} location={multi ? where : undefined} value={waste.lot} onChange={(lot) => setWaste({ ...waste, lot })} /> : null}
                <div className="chips">
                  {WASTE_REASONS.map((r) => (
                    <button key={r} type="button" className={`chip${waste.reason === t(r) ? ' active' : ''}`} onClick={() => setWaste({ ...waste, reason: t(r) })}>
                      {t(r)}
                    </button>
                  ))}
                </div>
                <div className="form-actions">
                  <Button variant="ghost" onClick={() => setWaste(null)}>
                    {t('common.cancel')}
                  </Button>
                  <Button variant="danger" icon="trash" onClick={recordWaste} busy={busy} disabled={!wasteN || waste.reason.trim().length < 3}>
                    {t('stock.waste_save')}
                  </Button>
                </div>
              </div>
            ) : null}
            <div className="list-card">{!moves ? <Skeleton h={80} /> : moves.length ? moves.map((m) => <MoveRow key={m.id} m={m} compact />) : <p className="muted pad">{t('stock.no_moves')}</p>}</div>
          </section>
        ) : null}
      </div>
    </Dialog>
  );
}

export type Costing = 'average' | 'fifo';

interface Layer {
  id: string;
  at: string;
  qty_in: number;
  qty_left: number;
  unit_cost: number;
  value: number;
  document: string | null;
  reason: string | null;
}

/** Under FIFO: the deliveries an item's stock is made of, oldest (next out) first. */
function CostLayers({ item }: { item: StockItem }) {
  const { data } = useAction<Layer[]>('inventory.layers', { item: item.id });
  if (!data?.length) return null;
  return (
    <section className="cost-layers" aria-labelledby="cost-layers-title">
      <h3 id="cost-layers-title">{t('stock.layers')}</h3>
      <p className="muted small">{t('stock.layers_hint')}</p>
      <ol className="cost-layer-list">
        {data.map((l, i) => (
          <li key={l.id} className={i === 0 ? 'next' : ''}>
            <span className="cost-layer-when">
              {i === 0 ? <span className="badge badge-info">{t('stock.layer_next')}</span> : null}
              {[dateTime(l.at), l.document ?? l.reason].filter(Boolean).join(' · ')}
            </span>
            <span className="tabular">{withUnit(l.qty_left, item.unit)}</span>
            <span className="tabular muted">{unitCost(l.unit_cost, item.unit)}</span>
            <strong className="tabular">{money(l.value)}</strong>
          </li>
        ))}
      </ol>
    </section>
  );
}

/** How stock is valued: one average cost per item, or first in, first out. */
function CostingDialog({ costing, since, onClose, onDone }: { costing: Costing; since: string | null; onClose: () => void; onDone: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [pick, setPick] = useState<Costing>(costing);
  const save = () =>
    void run(async () => {
      await action('inventory.set_costing', { costing: pick });
      await onDone();
      onClose();
    }, t('stock.costing_saved', { method: t(`stock.costing_${pick}`) }));
  return (
    <Dialog
      title={t('stock.costing')}
      onClose={onClose}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="check" onClick={save} busy={busy} disabled={pick === costing}>
            {t('stock.costing_switch')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <div className="choice-cards" role="radiogroup" aria-label={t('stock.costing')}>
          {(['average', 'fifo'] as const).map((c) => (
            <button key={c} type="button" role="radio" aria-checked={pick === c} className={`choice-card${pick === c ? ' active' : ''}`} onClick={() => setPick(c)}>
              <span className="choice-card-title">
                {t(`stock.costing_${c}`)}
                {c === costing ? <span className="badge badge-ok">{t('stock.costing_current')}</span> : null}
              </span>
              <span className="muted">{t(`stock.costing_${c}_hint`)}</span>
            </button>
          ))}
        </div>
        {since ? <p className="muted small">{t('stock.costing_since', { date: dateTime(since) })}</p> : null}
        <p className="muted small">{t('stock.costing_note')}</p>
      </div>
    </Dialog>
  );
}

/**
 * Goods moved from one location to another: one document, TR-n, out of one shelf and onto the
 * other. Nothing is bought or sold, so the books do not change.
 */
function TransferDialog({ places, from: start, onClose, onDone }: { places: Place[]; from: string; onClose: () => void; onDone: () => Promise<void> }) {
  const { busy, run } = useSave();
  const [from, setFrom] = useState(start);
  const [to, setTo] = useState(places.find((p) => p.id !== start)?.id ?? '');
  const [note, setNote] = useState('');
  const [lines, setLines] = useState([{ item: '', qty: '' }]);
  // What each item holds at the location goods leave from.
  const { data } = useAction<Overview>('inventory.overview', from ? { location: from } : {});
  const items = (data?.items ?? []).filter((i) => i.active);
  const byId = new Map(items.map((i) => [i.id, i]));
  const parsed = lines.map((l) => ({ item: byId.get(l.item), qty: parseQty(l.qty) }));
  const ready = parsed.filter((l) => l.item && l.qty && l.qty > 0);
  const tooMuch = parsed.find((l) => l.item && l.qty && l.qty > l.item.on_hand);
  const twice = new Set(ready.map((l) => l.item!.id)).size !== ready.length;
  const invalid = parsed.some((l, i) => (lines[i]!.qty || lines[i]!.item) && !(l.item && l.qty && l.qty > 0)) || !!tooMuch || twice || !from || !to || from === to;
  const name = (id: string) => places.find((p) => p.id === id)?.name ?? '';
  const swap = () => {
    setFrom(to);
    setTo(from);
  };
  const save = () =>
    void run(async () => {
      await action('inventory.transfer', { from, to, note: note.trim() || undefined, lines: ready.map((l) => ({ item: l.item!.id, qty: l.qty })) });
      await onDone();
      onClose();
    }, tn('stock.transferred', ready.length, { from: name(from), to: name(to) }));

  return (
    <Dialog
      title={t('stock.transfer')}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <span className={tooMuch ? 'text-danger' : 'muted'}>
            {tooMuch
              ? t('stock.transfer_too_much', { name: tooMuch.item!.name, qty: withUnit(tooMuch.item!.on_hand, tooMuch.item!.unit), place: name(from) })
              : twice
                ? t('stock.transfer_twice')
                : t('stock.transfer_hint')}
          </span>
          <Button variant="primary" icon="transfer" onClick={save} busy={busy} disabled={!ready.length || invalid}>
            {t('stock.transfer_save')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        <div className="transfer-route">
          <PlaceField places={places} value={from} onChange={setFrom} label={t('stock.transfer_from')} />
          <IconButton icon="transfer" label={t('stock.transfer_swap')} onClick={swap} />
          <PlaceField places={places} value={to} onChange={setTo} label={t('stock.transfer_to')} />
        </div>
        {from && from === to ? <p className="text-danger">{t('stock.transfer_same')}</p> : null}
        <div className="stock-lines transfer-lines" role="table" aria-label={t('stock.transfer_lines')}>
          <div className="stock-line head" role="row">
            <span role="columnheader">{t('stock.item')}</span>
            <span role="columnheader">{t('stock.quantity')}</span>
            <span role="columnheader" className="num">
              {t('stock.there_now', { place: name(from) })}
            </span>
            <span />
          </div>
          {lines.map((l, i) => {
            const p = parsed[i]!;
            const over = !!(p.item && p.qty && p.qty > p.item.on_hand);
            return (
              <div key={i} className={`stock-line${over ? ' field-error' : ''}`} role="row">
                <select
                  className="input"
                  aria-label={t('stock.item')}
                  value={l.item}
                  onChange={(e) => setLines(lines.map((x, j) => (j === i ? { ...x, item: e.target.value } : x)))}
                  data-autofocus={i === 0 || undefined}
                >
                  <option value="">{t('stock.pick_item')}</option>
                  {items.map((it) => (
                    <option key={it.id} value={it.id} disabled={it.on_hand <= 0}>
                      {it.name}
                    </option>
                  ))}
                </select>
                <span className="input-unit">
                  <input
                    className="input"
                    inputMode="decimal"
                    aria-label={t('stock.quantity')}
                    aria-invalid={over || undefined}
                    value={l.qty}
                    onChange={(e) => setLines(lines.map((x, j) => (j === i ? { ...x, qty: e.target.value } : x)))}
                    placeholder="0"
                  />
                  <span>{p.item ? unitLabel(p.item.unit) : ''}</span>
                </span>
                <span className="num">{p.item ? withUnit(p.item.on_hand, p.item.unit) : '—'}</span>
                <IconButton icon="trash" label={t('stock.remove_line')} disabled={lines.length === 1} onClick={() => setLines(lines.filter((_, j) => j !== i))} />
              </div>
            );
          })}
        </div>
        <Button icon="plus" className="add-line" onClick={() => setLines([...lines, { item: '', qty: '' }])}>
          {t('stock.add_line')}
        </Button>
        <Field label={t('stock.transfer_note')}>
          <input className="input" value={note} maxLength={200} onChange={(e) => setNote(e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}
